import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-datev-schnittstelle";
const TITLE = "Shopify DATEV Schnittstelle: Buchhaltung sauber anbinden";
const DESCRIPTION =
  "Shopify an DATEV anbinden: Belegexport über Apps wie BuchhaltungsButler oder Pathway, Sammelrechnungen, Gutschriften und wann eine individuelle Schnittstelle sinnvoll ist.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Die Verbindung von Shopify und DATEV läuft in der Praxis über Buchhaltungs-Apps, die Bestellungen als Belege exportieren und für DATEV Rechnungsdatenservice aufbereiten. Für die meisten Shops reicht eine etablierte App, korrekt eingerichtet mit Steuersätzen, Gutschriften und Sammelbuchen. Eine individuelle Schnittstelle lohnt erst bei Sonderlogik, etwa eigenen Erlöskonten pro Vertriebsweg oder automatisiertem Abgleich mehrerer Shops.";

const faqs = [
  {
    q: "Wie verbinde ich Shopify mit DATEV?",
    a: "Über eine Buchhaltungs-App aus dem Shopify App Store, etwa BuchhaltungsButler, Pathway oder vergleichbare Lösungen. Die App erzeugt aus Bestellungen und Gutschriften Belege und exportiert sie im DATEV-Format an Ihr Steuerbüro.",
  },
  {
    q: "Was kostet die DATEV-Anbindung für Shopify?",
    a: "Apps liegen je nach Belegvolumen meist zwischen 20 und 100 € im Monat. Die Einrichtung mit Kontenzuordnung, Steuersätzen und Testlauf dauert erfahrungsgemäß einige Stunden und wird nach Aufwand zu 110 € netto pro Stunde berechnet.",
  },
  {
    q: "Einzelbelege oder Sammelbuchungen?",
    a: "Das entscheidet Ihr Steuerbüro. Viele Shops buchen tägliche Sammelbelege pro Zahlungsart, um das Belegvolumen klein zu halten. Wichtig ist, dass die Zuordnung von Erlösen, Gebühren und Erstattungen nachvollziehbar bleibt.",
  },
  {
    q: "Wie werden Gutschriften und Stornos behandelt?",
    a: "Eine sauber eingerichtete App erzeugt aus Retouren und Stornos eigene Belege und verrechnet sie mit der ursprünglichen Buchung. Genau hier entstehen bei hastiger Einrichtung die meisten Fehler.",
  },
  {
    q: "Wann brauche ich eine individuelle Schnittstelle?",
    a: "Wenn Standard-Apps Ihre Logik nicht abbilden: mehrere Shops mit getrennten Kontenrahmen, eigene B2B-Preislogik, Abgleich mit einem ERP, das selbst keine DATEV-Anbindung hat. Dann ist eine Custom App der sauberere Weg.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-datev-schnittstelle")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Schnittstellen",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Schnittstellen"
      title="Shopify und DATEV verbinden, ohne Buchhaltungschaos"
      intro="Die DATEV-Anbindung ist selten ein Technikproblem und fast immer ein Einrichtungsproblem: Steuersätze, Gutschriften, Gebühren. Dieser Überblick zeigt die Wege, die typischen Fehler und wann eine eigene Schnittstelle sinnvoll ist."
      source="ratgeber-shopify-datev"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Der Standardweg: Buchhaltungs-App",
          p: [
            "Etablierte Apps wie BuchhaltungsButler oder Pathway holen Bestellungen aus Shopify, erzeugen Belege und liefern sie im DATEV-Format an das Steuerbüro. Für einen Shop mit Standardsortiment ist das der pragmatische Weg.",
            "Entscheidend ist die Einrichtung: korrekte Steuersätze pro Produktgruppe, Zuordnung der Zahlungsarten mit ihren Gebühren, Behandlung von Gutscheinen und Versandkosten. Eine App, die falsch eingerichtet ist, produziert Fehler in Serie.",
          ],
        },
        {
          h: "Wo es in der Praxis klemmt",
          p: [
            "Diese Punkte verursachen die meisten Rückfragen vom Steuerbüro und lassen sich bei der Einrichtung vermeiden.",
          ],
          list: [
            "Gutschriften und Teilretouren ohne Bezug zur ursprünglichen Buchung",
            "Gebühren von Shopify Payments, PayPal oder Klarna, die nicht als eigene Position ankommen",
            "Gemischte Steuersätze im Sortiment, die pauschal auf einen Satz gebucht werden",
            "B2B-Bestellungen mit Nettopreisen, die als Bruttobeleg exportiert werden",
            "Mehrere Märkte oder Währungen ohne saubere Kontentrennung",
          ],
        },
        {
          h: "Sammelbelege statt Belegflut",
          p: [
            "Shops mit vielen kleinen Bestellungen buchen häufig tägliche Sammelbelege pro Zahlungsart. Das hält das Belegvolumen beim Steuerbüro klein und die Kosten der App niedrig, weil viele Tarife nach Belegzahl abrechnen.",
            "Ob Einzelbeleg oder Sammelbuchung entscheidet Ihr Steuerbüro. Die Technik kann beides, die Absprache gehört an den Anfang des Projekts.",
          ],
        },
        {
          h: "Wann eine eigene Schnittstelle sinnvoll ist",
          p: [
            "Standard-Apps enden bei Sonderlogik: getrennte Kontenrahmen für mehrere Shops, Abgleich mit einem ERP ohne DATEV-Anbindung, eigene Belegformate oder automatisierte Kontrollen zwischen Bestellung und Buchung.",
            "Dann ist eine Custom App der sauberere Weg: Sie läuft nur für Ihren Shop, braucht kein App-Store-Review und bildet genau die Logik ab, die Ihr Steuerbüro freigegeben hat.",
          ],
        },
        {
          h: "Ablauf und Kosten",
          p: [
            "Einrichtung einer Standard-App inklusive Testlauf und Abstimmung mit Ihrem Steuerbüro dauert erfahrungsgemäß wenige Stunden und wird mit 110 € netto pro Stunde berechnet. Eine individuelle Schnittstelle schätze ich nach Sichtung der Anforderungen mit festem Rahmen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-schnittstellen", label: "Shopify Schnittstellen im Überblick" },
        { to: "/shopify-app-entwicklung", label: "Individuelle Schnittstelle bauen lassen" },
        { to: "/shopify-entwickler", label: "Shopify Entwickler beauftragen" },
        { to: "/kontakt", label: "DATEV-Anbindung anfragen" },
      ]}
    />
  );
}
